import { Injectable, signal, computed } from '@angular/core';
import { Subject } from 'rxjs';
import { StorageService } from './storage/storage.service';
import { ConnectionService } from './connection.service';

const STORAGE_KEY = 'userOfflineMode';
// Synchroner localStorage-Key als Cache für sofortige Verfügbarkeit beim App-Start.
// localForage (async) wird weiterhin als primärer Speicher verwendet.
const SYNC_STORAGE_KEY = 'bp_userOfflineMode_sync';

@Injectable({
	providedIn: 'root'
})
export class OfflineModeService {
	private _userOfflineMode = signal<boolean>(OfflineModeService.readSyncValue());
	private _networkOnline = signal<boolean>(true);
	private _initialized = signal<boolean>(false);

	/**
	 * Liest den Offline-Modus synchron aus localStorage.
	 * Wird als Initialwert für das Signal verwendet, damit der Wert
	 * sofort in Service-Konstruktoren verfügbar ist (BEVOR initFromStorage() läuft).
	 */
	private static readSyncValue(): boolean {
		try {
			return localStorage.getItem(SYNC_STORAGE_KEY) === 'true';
		} catch {
			return false;
		}
	}

	private static writeSyncValue(value: boolean): void {
		try {
			localStorage.setItem(SYNC_STORAGE_KEY, String(value));
		} catch {
			// localStorage nicht verfügbar (z.B. private mode in manchen Browsern)
		}
	}

	// offlineMode ist NUR true wenn der Benutzer explizit in den Offline-Modus gewechselt hat.
	// Netzwerk-Unterbrechung schaltet NICHT automatisch in den Offline-Modus!
	readonly offlineMode = computed(() => this._userOfflineMode());
	readonly userOfflineMode = computed(() => this._userOfflineMode());
	readonly networkOnline = computed(() => this._networkOnline());
	readonly initialized = computed(() => this._initialized());

	// Event wenn sich der Offline-Status ändert
	readonly offlineModeChanged = new Subject<boolean>();

	// Event wenn sich der Netzwerk-Status ändert
	readonly networkStatusChanged = new Subject<boolean>();

	private lastOfflineMode: boolean | undefined;
	private lastNetworkOnline: boolean | undefined;

	constructor(
		private storage: StorageService,
		private cs: ConnectionService
	) {
		this.cs.monitor().subscribe(online => {
			this._networkOnline.set(online);
			this.checkAndEmitChange();
			this.checkAndEmitNetworkChange();
		});
	}

	private checkAndEmitChange(): void {
		const current = this.offlineMode();
		if (this.lastOfflineMode !== current) {
			this.lastOfflineMode = current;
			this.offlineModeChanged.next(current);
		}
	}

	private checkAndEmitNetworkChange(): void {
		const current = this._networkOnline();
		if (this.lastNetworkOnline !== current) {
			this.lastNetworkOnline = current;
			this.networkStatusChanged.next(current);
		}
	}

	async initFromStorage(): Promise<boolean> {
		const stored = await this.storage.get(STORAGE_KEY);
		const isOffline = stored === true;
		this._userOfflineMode.set(isOffline);
		OfflineModeService.writeSyncValue(isOffline);
		this._initialized.set(true);
		return isOffline;
	}

	async setOfflineMode(offline: boolean): Promise<void> {
		this._userOfflineMode.set(offline);
		OfflineModeService.writeSyncValue(offline);
		await this.storage.set(STORAGE_KEY, offline);
		this.checkAndEmitChange();
	}

	isOffline(): boolean {
		return this.offlineMode();
	}

	isNetworkOnline(): boolean {
		return this._networkOnline();
	}

	canGoOnline(): boolean {
		return this._networkOnline();
	}
}
